import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, Target, Heart, Lightbulb, ArrowRight, GraduationCap, Briefcase, Rocket, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We strive for perfection in every project, delivering exceptional quality that exceeds expectations.',
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'Our love for digital innovation drives us to create meaningful experiences for brands.',
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'We work closely with our clients as partners, ensuring every solution aligns with their vision.',
    },
    {
      icon: Award,
      title: 'Innovation',
      description: 'We stay ahead of trends, leveraging cutting-edge technology to deliver future-ready solutions.',
    },
  ];

  const goals = [
    {
      icon: TrendingUp,
      title: 'Deliver High-Quality Services',
      description: 'Provide digital and creative services that drive measurable results for our clients.',
    },
    {
      icon: Users,
      title: 'Build Strong Partnerships',
      description: 'Foster long-term relationships with clients through transparency and consistent performance.',
    },
    {
      icon: Lightbulb,
      title: 'Continuous Innovation',
      description: 'Stay ahead with modern marketing tools, trends, and technologies.',
    },
  ];

  const milestones = [
    {
      year: "2009",
      title: "Global Recognition",
      description: "Represented India at ASME, USA, gaining global exposure and developing a strong innovation mindset."
    },
    {
      year: "2010",
      title: "Corporate Beginnings",
      description: "Started corporate career at HCL Technologies, building technical and professional foundations."
    },
    {
      year: "2013",
      title: "Entrepreneurial Leap",
      description: "Quit corporate life to pursue entrepreneurship with a vision to transform brands."
    },
    {
      year: "2014",
      title: "Marketing Foundation",
      description: "Entered the traditional printing and marketing industry, learning core branding from the ground up."
    },
    {
      year: "2017",
      title: "Food Venture",
      description: "Founded Veerjees, introducing convenient early-morning food solutions with a QSR concept."
    },
    {
      year: "2019",
      title: "Healthcare Scale-Up",
      description: "Scaled Dental Planet from 1 to 15 clinics through strategic marketing and growth systems."
    },
    {
      year: "2022",
      title: "Leadership Role",
      description: "Became Director of Marketing & Operations at Dental Planet Multisolutions, driving growth and operational excellence."
    },
    {
      year: "2023",
      title: "Meta Acknowledgment",
      description: "Earned recognition from Meta for excellence in ROI-based digital advertising."
    },
    {
      year: "2024",
      title: "European Expansion",
      description: "Explored business opportunities across Europe for global expansion and advanced learning."
    },
    {
      year: "2025",
      title: "Educational Venture",
      description: "Launched Bluemarks Education, empowering students for MBBS Abroad with ethical consultancy practices."
    },
  ];

  return (
    <div className="pt-20 overflow-x-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-bold mb-6">
              About <span className="text-gradient">Color Drop</span>
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed">
              Color Drop Business Solutions (CDBS) is a leading digital marketing, creative, and IT solutions agency founded in 2013. Built on the vision of blending creativity, technology, and strategic expertise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About CDBS Section */}
      <section className="py-24 bg-neutral-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: false }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-serif font-bold mb-6">About Us</h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: false }}
              className="prose prose-lg max-w-none"
            >
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
              Color Drop Business Solutions (CDBS) is a Leading Digital Marketing, Creative, and IT Solutions Agency founded in 2013 by Jatin Puri. Built on the vision of blending Creativity, Technology, and Strategic Expertise, CDBS helps businesses grow, strengthen their Brand Identity, and achieve Measurable Results.
              </p>

              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
              We offer a complete suite of services including Digital Marketing, Social Media Management, Performance Advertising, Branding, Graphic Designing, Business Consulting, Website Development, and Mobile App Development. Our IT division focuses on creating Fast, Secure, User-Friendly Websites and Apps tailored to each client's goals, ensuring a strong Digital Presence and seamless Customer Experience.
              </p>

              <p className="text-lg text-neutral-600 mb-8 leading-relaxed">
              Recognized by Meta for excellence in Digital Advertising, we continuously evolve with new tools, technologies, and industry trends. Over the years, we have become Central India's Emerging Digital Marketing and IT Solutions Company, trusted for Quality, Innovation, and Consistent Performance.
              </p>

              <div className="bg-white rounded-xl p-8 shadow-lg mb-8">
                <h3 className="text-2xl font-serif font-bold mb-6 text-center">Our Process</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  <div>
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white text-2xl font-bold">1</span>
                    </div>
                    <h4 className="font-semibold text-lg mb-2">Understand</h4>
                  </div>
                  <div>
                    <div className="w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white text-2xl font-bold">2</span>
                    </div>
                    <h4 className="font-semibold text-lg mb-2">Create</h4>
                  </div>
                  <div>
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white text-2xl font-bold">3</span>
                    </div>
                    <h4 className="font-semibold text-lg mb-2">Deliver</h4>
                  </div>
                  <div>
                    <div className="w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white text-2xl font-bold">4</span>
                    </div>
                    <h4 className="font-semibold text-lg mb-2">Grow</h4>
                  </div>
                </div>
              </div>

              <p className="text-lg text-neutral-600 leading-relaxed text-center">
                At CDBS, our process is simple and effective: <strong>Understand. Create. Deliver. Grow.</strong>
              </p>
              <p className="text-lg text-neutral-600 leading-relaxed text-center mt-4">
                We believe in meaningful collaborations, transparent communication, and long-term success for every brand we partner with.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: false }}
              className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8"
            >
              <div className="flex items-center mb-6">
                <Target className="w-10 h-10 text-primary-600 mr-4" />
                <h2 className="text-3xl font-serif font-bold">Our Mission</h2>
              </div>
              <p className="text-lg text-neutral-700 leading-relaxed">
                To empower businesses with creative, data-driven, and result-oriented digital solutions that enhance brand value, accelerate growth, and build long-term customer trust.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: false }}
              className="bg-gradient-to-br from-accent-50 to-accent-100 rounded-2xl p-8"
            >
              <div className="flex items-center mb-6">
                <Rocket className="w-10 h-10 text-accent-600 mr-4" />
                <h2 className="text-3xl font-serif font-bold">Our Vision</h2>
              </div>
              <p className="text-lg text-neutral-700 leading-relaxed">
                To become Central India's most trusted and innovative digital marketing and business solutions company—where creativity meets technology, and every brand finds its unique identity and growth path.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Goals */}
      <section className="py-24 bg-neutral-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Our Goals</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Our commitment to excellence drives us toward these key objectives.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {goals.map((goal, index) => (
              <motion.div
                key={goal.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center mb-4">
                  <goal.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{goal.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{goal.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Our Core Values</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              These principles guide everything we do and ensure we deliver exceptional results for our clients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="text-center group"
              >
                <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <value.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4">{value.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Our Founder</h2>
            <img src="/founder.jpg" alt="Jatin Puri" className="md:w-[80%] h-full object-cover rounded-2xl mx-auto mb-6" />
            <h3 className="text-3xl font-serif font-semibold mb-4">Jatin Puri</h3>
            <p className="text-xl text-primary-600 font-medium mb-8">Founder of Color Drop Business Solutions (CDBS)</p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: false }}
              className="prose prose-lg max-w-none"
            >
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
              Jatin Puri is the Founder of Color Drop Business Solutions (CDBS), an Emerging Digital and Creative Agency established in 2013. His Journey blends Technical Expertise, Global Exposure, and a Strong Entrepreneurial Vision.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <GraduationCap className="w-8 h-8 text-primary-600 mr-3" />
                    <h4 className="text-xl font-semibold">Education & Achievement</h4>
                  </div>
                  <p className="text-neutral-600 leading-relaxed">
                    After completing his B.Tech, Jatin represented India at the ASME (American Society of Mechanical Engineers), USA) in 2009, an achievement that broadened his perspective and strengthened his passion for innovation.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <Briefcase className="w-8 h-8 text-accent-600 mr-3" />
                    <h4 className="text-xl font-semibold">Professional Journey</h4>
                  </div>
                  <p className="text-neutral-600 leading-relaxed">
                    He began his professional career with HCL Technologies in 2010, gaining valuable corporate experience. But with a larger dream in mind, he left his job in 2013 to build his own company.
                  </p>
                </div>
              </div>

              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
              With Deep Expertise in Marketing Strategy, Business Consulting, Brand Development, and Performance Marketing, Jatin has helped Numerous Brands Scale through Digital Transformation. His Work has also earned him Recognition from Meta for Excellence in Digital Advertising.
              </p>

              <p className="text-lg text-neutral-600 leading-relaxed">
              Under his Leadership, CDBS has grown into Central India's Emerging Digital Marketing and Business Solutions Company, known for its Creative Approach, Customer-Focused Process, and Measurable Results.
              </p>

              <div className="mt-8 bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl p-6 text-white">
                <p className="text-lg font-medium italic">
                "Driven by Passion, Innovation, and the Belief that Every Business can Grow with the Right Strategy, Jatin continues to lead CDBS toward New Milestones."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-neutral-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Our Journey</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              From humble beginnings to becoming Central India's emerging digital marketing company.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className={`flex items-center mb-12 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-xl font-semibold mb-2">{milestone.title}</h3>
                    <p className="text-neutral-600">{milestone.description}</p>
                  </div>
                </div>
                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {milestone.year}
                </div>
                <div className="flex-1"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-primary-500 to-accent-500">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
          >
            <h2 className="text-4xl font-serif font-bold text-white mb-6">
              Let's Build Your Brand's Next Big Chapter
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Together, we can create exceptional digital experiences that drive growth and success.
            </p>
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-accent-50 transition-colors duration-300 inline-flex items-center"
            >
              Start a Project
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
